// Документы по сделке: счета клиенту, счета на оплату, договоры, отчёты. ТОЛЬКО ЧТЕНИЕ.
//
// НА КАКОЙ ВОПРОС ОТВЕЧАЕТ ЭКРАН, КОТОРОГО НЕ БЫЛО
// «Где сохраняются счета на оплату, как они логируются, регистрация договоров» —
// владелец назвал это среди обязательного. Соседние экраны /smart/* отвечают наполовину:
// каждый показывает ОДИН тип и читает живой Bitrix, которого через месяц не будет.
// Вопрос «что по этой сделке выставлено, подписано и сдано» в них нельзя задать вовсе —
// человек обходит четыре экрана руками и каждый раз рискует не заметить, что где-то
// пусто. Здесь четыре типа читаются из зеркала одним списком, и есть разрез по сделке.
//
// ЧТО ЭКРАН ОБЯЗАН СКАЗАТЬ ВСЛУХ, ИНАЧЕ ОН СОВРЁТ ЦИФРОЙ (замеры считает ядро)
//   • ОТСЕЧКА. Показываются документы с 01.01.2025 — так попросил владелец. В зеркале
//     их больше, и число отрезанных стоит в оговорке. Молча показать часть нельзя.
//   • СУММЫ НЕТ — ЭТО НЕ НОЛЬ. У договоров суммы нет вовсе, у отчётов по сделкам она
//     заполнена у трёх из десяти. «0 ₽» в этой колонке было бы враньём цифрой, поэтому
//     там прочерк, а рядом с каждой суммой стоит, у скольких строк её нет.
//   • НОЛЬ — ЭТО СУММА. Документов, выставленных ровно на ноль, три сотни. Прятать их
//     прочерком нельзя: ровно ради них сводку и открывают.
//   • СКАНОВ ЗДЕСЬ НЕТ. Счёт-файл, УПД, чеки, подписанный договор, файл платёжки —
//     фотографии финансовых документов и ссылки на них в зеркало не переносятся вовсе.
//     Карточка называет, СКОЛЬКО таких полей вырезано и как они называются, но не
//     показывает ни значения, ни адреса. Молчание тут читалось бы как «зеркало потеряло».
//   • ИСТОРИЯ СТАДИЙ НАЧИНАЕТСЯ НЕ С НАЧАЛА. Переходы синк читает с января 2026-го,
//     а документы показаны с 2025-го. Пустая история значит «мы не видели», а не
//     «стадию не меняли», и карточка печатает границы охвата рядом.
//
// ПОЧЕМУ ДВА ПОЛЯ ПОИСКА, А НЕ ОДНО
// Номер документа лежит внутри jsonb, и поиск по нему стоит 1,9 с против 0,3 с у поиска
// по названию (замер по боевому зеркалу). Одно общее поле платило бы эту разницу на
// каждый запрос, включая те, где номера заведомо ни при чём. Разделить по виду строки
// было нельзя: номера с буквами существуют и их много.
//
// ЗАПИСИ ЗДЕСЬ НЕТ И НЕ БУДЕТ БЕЗ ОТДЕЛЬНОГО РЕШЕНИЯ ВЛАДЕЛЬЦА: на полях этих типов
// в Bitrix висят бизнес-процессы, которые ставят задачи живым людям.
import { useMemo, useState } from 'react'

import {
  useДокументы, useИтогиДокументов, useКарточкаДокумента, useСводкаДокументов,
  useСправочникиДокументов, ИМЯ_СУЩНОСТИ, РАЗМЕР_СТРАНИЦЫ, самаяСтарая,
} from '../api/docs'
import type {
  ГруппаСводки, ДокументСтрока, ИтогиСписка, КарточкаДокумента, СправочникиДокументов,
  ТипДокумента,
} from '../api/docs'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, plural, pluralN } from '../lib/format'

type Вкладка = 'list' | 'summary'

export default function Docs() {
  const [вкладка, setВкладка] = useState<Вкладка>('list')
  const refs = useСправочникиДокументов()

  const [тип, setТип] = useState('')
  const [стадия, setСтадия] = useState('')
  const [поиск, setПоиск] = useState('')
  const [номер, setНомер] = useState('')
  const [сДаты, setСДаты] = useState('')
  const [поДату, setПоДату] = useState('')
  const [сделка, setСделка] = useState('')
  const [сортировка, setСортировка] = useState('created')
  const [start, setStart] = useState(0)

  // Стадии показываем только выбранного типа: коды у типов свои, и общий список из
  // тридцати шести стадий вперемешку читался бы как «стадии документов», хотя
  // «Оплачен» у счёта клиенту и «Оплачен» у счёта поставщику — разные стадии.
  const стадииТипа = useMemo(() => {
    const типы = refs.data?.types ?? []
    const свой = типы.find((т) => т.kind === тип)
    return свой ? свой.stages : []
  }, [refs.data, тип])

  const отбор = useMemo(() => ({
    type: тип || undefined,
    stage: тип && стадия ? стадия : undefined,
    q: поиск.trim().length >= 2 ? поиск.trim() : undefined,
    num: номер.trim() ? номер.trim() : undefined,
    date_from: сДаты || undefined,
    date_to: поДату || undefined,
    deal: сделка.trim() || undefined,
  }), [тип, стадия, поиск, номер, сДаты, поДату, сделка])

  const список = useДокументы({ ...отбор, sort: сортировка, start, limit: РАЗМЕР_СТРАНИЦЫ })
  const итоги = useИтогиДокументов(отбор)
  // Сводка спрашивается только когда её открыли: она ходит в те же денежные поля внутри
  // jsonb и стоит около 650 мс — платить их тому, кто пришёл посмотреть список, не за что.
  const сводка = useСводкаДокументов(отбор, вкладка === 'summary')

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }
  const св = самаяСтарая(refs.data?.freshness)

  return (
    <>
      <div className="page-head">
        <h1>Документы по сделке</h1>
        <span className="spacer" />
        <span className="hint">
          {св?.age_min != null
            ? `${ИМЯ_СУЩНОСТИ[св.entity] ?? св.entity}: обновлено `
              + (св.age_min < 1 ? 'только что' : `${Math.round(св.age_min)} мин назад`)
            : 'свежесть неизвестна'}
        </span>
      </div>

      <Оговорки refs={refs.data} />

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {([['list', 'Реестр'], ['summary', 'Сводка по стадиям']] as [Вкладка, string][])
          .map(([код, подпись]) => (
            <button key={код} onClick={() => setВкладка(код)}
                    aria-pressed={вкладка === код}
                    style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
              {подпись}
            </button>
          ))}
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        {/* Поля идут блоками `.f` с отдельным <label htmlFor>: FilterBar берёт подпись
            из textContent метки, и у метки, внутри которой лежит список, в textContent
            попал бы весь справочник стадий. */}
        <FilterBar id="docs-list">
          <div className="f">
            <label htmlFor="docs-type">Вид документа</label>
            <select id="docs-type" value={тип}
                    onChange={(e) => { сСброса(setТип)(e.target.value); setСтадия('') }}>
              <option value="">все виды</option>
              {(refs.data?.types ?? []).map((т) => (
                <option key={т.kind} value={т.kind}>{т.title} · {т.docs}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="docs-stage">Стадия</label>
            <select id="docs-stage" value={стадия} disabled={!тип}
                    onChange={(e) => сСброса(setСтадия)(e.target.value)}>
              <option value="">{тип ? 'все стадии' : 'сначала выберите вид'}</option>
              {стадииТипа.map((с) => (
                <option key={с.stage_id} value={с.stage_id}>{с.name ?? с.stage_id}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="docs-q">Название или компания</label>
            <input id="docs-q" type="search" value={поиск} placeholder="Морвенна, Грифон…"
                   onChange={(e) => сСброса(setПоиск)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="docs-num">Номер документа</label>
            <input id="docs-num" type="search" value={номер} placeholder="32677, № 6286, б/н"
                   onChange={(e) => сСброса(setНомер)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="docs-from">Дата документа с</label>
            <input id="docs-from" type="date" value={сДаты}
                   onChange={(e) => сСброса(setСДаты)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="docs-to">по</label>
            <input id="docs-to" type="date" value={поДату}
                   onChange={(e) => сСброса(setПоДату)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="docs-deal">Номер сделки</label>
            <input id="docs-deal" type="search" value={сделка} placeholder="109538"
                   onChange={(e) => сСброса(setСделка)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="docs-sort">Порядок</label>
            <select id="docs-sort" value={сортировка}
                    onChange={(e) => сСброса(setСортировка)(e.target.value)}>
              <option value="created">когда завели</option>
              <option value="date">по дате документа</option>
              <option value="due">по сроку</option>
              <option value="amount">по сумме</option>
              <option value="updated">когда трогали</option>
            </select>
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          {/* Поиск по номеру дороже поиска по названию, и человек имеет право знать,
              почему именно этот запрос думает дольше. */}
          {номер.trim()
            ? 'поиск по номеру читает поле внутри карточки документа и отвечает дольше '
              + 'обычного — так устроено хранилище Bitrix'
            : 'поиск по названию и компании; номер документа ищется отдельным полем'}
        </div>
      </div>

      {вкладка === 'list' ? (
        <QueryState isLoading={список.isLoading} error={список.error}>
          <Итоги запрос={итоги} />
          <Таблица строки={список.data?.rows ?? []} refs={refs.data} />
          <Страницы всего={список.data?.total ?? 0} start={start} setStart={setStart} />
        </QueryState>
      ) : (
        <QueryState isLoading={сводка.isLoading} error={сводка.error}>
          <СводкаПоСтадиям группы={сводка.data?.groups ?? []} />
        </QueryState>
      )}
    </>
  )
}

// ═══════════════════════════════ оговорки ═══════════════════════════════
//
// Тексты и числа в них считает ядро. Своих чисел здесь нет ни одного намеренно: вбитая
// цифра однажды разойдётся с тем, что человек видит в таблице рядом, и первым он
// перестанет верить именно таблице.

function Оговорки({ refs }: { refs?: СправочникиДокументов }) {
  const тексты = refs?.caveats ?? []
  if (!тексты.length) return null
  return (
    <div className="notice" style={{ marginBottom: 12 }}>
      <b>Что этот раздел показывает и чего в нём нет.</b>
      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
        {тексты.map((т, i) => <li key={i} style={{ marginBottom: 4 }}>{т}</li>)}
      </ul>
    </div>
  )
}

// ═══════════════════════════════ итоги ═══════════════════════════════

function Итоги({ запрос }: { запрос: { data?: { totals: ИтогиСписка }; isLoading: boolean } }) {
  const и = запрос.data?.totals
  if (!и) {
    return (
      <div className="hint" style={{ marginBottom: 12 }}>
        {запрос.isLoading ? 'считаем итоги по всей выборке…' : 'итоги посчитать не удалось'}
      </div>
    )
  }
  const всего = и.docs ?? 0
  const безСуммы = и.no_amount ?? 0
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">документов под отбором</div>
        <div className="t-sum">{всего}</div>
        <div className="t-label">
          {(и.types ?? 0) === 1
            ? 'один вид документов'
            : `${pluralN(и.types ?? 0, 'вид', 'вида', 'видов')} документов`}
        </div>
      </div>
      <div className="tile">
        <div className="t-label">на сумму</div>
        <div className="t-sum">{fmtMoney(и.amount_sum)}</div>
        {/* Сумма без числа строк, у которых её нет, читалась бы как полная. */}
        <div className="t-label">
          {безСуммы > 0
            ? `у ${безСуммы} ${plural(безСуммы, 'документа', 'документов', 'документов')}`
              + ' суммы нет вовсе — в итог они не входят'
            : 'сумма есть у всех документов отбора'}
        </div>
      </div>
      <div className="tile">
        <div className="t-label">выставлено ровно на ноль</div>
        <div className="t-sum">{и.zero_amount ?? 0}</div>
        <div className="t-label">это сумма, а не пропуск: прочерком их не прячем</div>
      </div>
      <div className="tile">
        <div className="t-label">сделок затронуто</div>
        <div className="t-sum">{и.deals ?? 0}</div>
        <div className="t-label">
          {(и.no_deal ?? 0) > 0
            ? `${pluralN(и.no_deal ?? 0, 'документ', 'документа', 'документов')} без сделки`
            : 'все документы привязаны к сделке'}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════ таблица ═══════════════════════════════

function Таблица({ строки, refs }: { строки: ДокументСтрока[]; refs?: СправочникиДокументов }) {
  const [открыт, setОткрыт] = useState<{ kind: string; id: number } | null>(null)
  const типПоНомеру = useMemo(() => {
    const м = new Map<number, ТипДокумента>()
    for (const т of refs?.types ?? []) м.set(т.type, т)
    return м
  }, [refs])

  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Документов под этот отбор нет</div>
        <div className="empty-hint">
          Раздел показывает документы, заведённые начиная с {refs?.cutoff ?? 'даты отсечки'}:
          более ранние сюда не попадают вовсе. Снимите фильтр по виду или стадии, а номер
          документа ищите отдельным полем — по названию он не находится.
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Документ</th>
              <th>Стадия</th>
              <th className="num">Сумма</th>
              <th>Дата</th>
              <th>Компания</th>
              <th>Сделка</th>
              <th>Ответственный</th>
            </tr>
          </thead>
          <tbody>
            {строки.map((с) => {
              const т = типПоНомеру.get(с.entity_type_id)
              return (
                <tr key={`${с.entity_type_id}:${с.bitrix_id}`}>
                  <td data-label="Документ">
                    {/* Номер документа — то, чем его называют вслух и что написано на
                        бумаге. Где номера нет вовсе (отчёты по сделкам), показываем id:
                        по чему-то же надо открыть карточку. */}
                    <button type="button"
                            onClick={() => setОткрыт({ kind: т?.kind ?? String(с.entity_type_id),
                                                       id: с.bitrix_id })}>
                      {с.doc_number ? `№ ${с.doc_number}` : `#${с.bitrix_id}`}
                    </button>
                    <div className="muted">{т?.title ?? `тип ${с.entity_type_id}`}</div>
                    <div className="muted">{с.title ?? ''}</div>
                  </td>
                  <td data-label="Стадия">
                    {(с.stage_id && refs?.stage_names?.[с.stage_id]) || с.stage_id || '—'}
                  </td>
                  {/* Прочерк здесь значит «суммы у этого вида документов нет вовсе»,
                      а ноль — что документ выставлен ровно на ноль. Это разные ответы. */}
                  <td className="num" data-label="Сумма">
                    {с.amount === null || с.amount === undefined
                      ? <span className="muted" title={т && !т.has_money
                          ? 'у этого вида документов суммы нет вовсе'
                          : 'сумма у этого документа не заполнена'}>—</span>
                      : fmtMoney(с.amount, с.currency ?? 'RUB')}
                  </td>
                  <td data-label="Дата">
                    {fmtDate(с.doc_date)}
                    {с.due_date && <div className="muted">срок {fmtDate(с.due_date)}</div>}
                    {с.paid_date && <div className="muted">оплачен {fmtDate(с.paid_date)}</div>}
                  </td>
                  <td data-label="Компания">
                    <EntityLink kind="company" id={с.company_id} title={с.company_title} />
                  </td>
                  <td data-label="Сделка">
                    {с.parent_deal_id
                      ? <EntityLink kind="deal" id={с.parent_deal_id} title={с.deal_title} />
                      : <span className="muted">не привязан</span>}
                    {с.deal_gone && (
                      <div className="muted">сделки ещё нет в зеркале</div>
                    )}
                  </td>
                  <td data-label="Ответственный">{с.assigned_name || '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {открыт && (
        <Карточка тип={открыт.kind} ид={открыт.id} закрыть={() => setОткрыт(null)} />
      )}
    </>
  )
}

function Страницы({ всего, start, setStart }: {
  всего: number; start: number; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0}
              onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
        назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={start + РАЗМЕР_СТРАНИЦЫ >= всего}
              onClick={() => setStart(start + РАЗМЕР_СТРАНИЦЫ)}>
        дальше
      </button>
    </div>
  )
}

// ═══════════════════════════════ карточка ═══════════════════════════════

function Карточка({ тип, ид, закрыть }: { тип: string; ид: number; закрыть: () => void }) {
  const запрос = useКарточкаДокумента(тип, ид)
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <b>{запрос.data ? запрос.data.title : 'Документ'} №{ид}</b>
        <span className="spacer" />
        <button onClick={закрыть}>закрыть</button>
      </div>
      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {запрос.data && <ТелоКарточки к={запрос.data} />}
      </QueryState>
    </div>
  )
}

function ТелоКарточки({ к }: { к: КарточкаДокумента }) {
  const охват = к.stage_history_coverage
  return (
    <>
      <div className="hint" style={{ marginBottom: 8 }}>
        {к.what} · вторая сторона — {к.side}
        {к.doc.synced_at ? ` · в зеркале с ${fmtDate(к.doc.synced_at, true)}` : ''}
      </div>

      {/* Названия вырезанных полей есть, значений и адресов — нет. Молча вырезать поле
          нельзя: человек, который знает, что скан в Bitrix есть, решит, что зеркало его
          потеряло, и пойдёт проверять синк. */}
      {к.hidden_fields.count > 0 && (
        <div className="notice" style={{ marginBottom: 8 }}>
          <b>Файлов и ссылок в этой карточке нет намеренно.</b> {к.hidden_fields.why}
          {' '}У этого вида документов таких полей {к.hidden_fields.count}
          {к.hidden_fields.filled_here > 0
            ? `, у этого документа заполнено ${к.hidden_fields.filled_here}`
            : ', у этого документа не заполнено ни одного'}.
          <div className="muted" style={{ marginTop: 4 }}>
            {к.hidden_fields.names.join(' · ')}
          </div>
        </div>
      )}

      <div className="table-wrap cards" style={{ marginBottom: 12 }}>
        <table>
          <thead>
            <tr><th>Поле</th><th>Значение</th><th className="num">Заполнено у</th></tr>
          </thead>
          <tbody>
            {к.fields.map((п) => (
              <tr key={п.code}>
                <td data-label="Поле">{п.title}</td>
                <td data-label="Значение">{показать(п.value)}</td>
                {/* Заполненность поля по всему типу: она объясняет, почему соседнее поле
                    пусто — его просто не ведут, а не потеряли при переносе. */}
                <td className="num" data-label="Заполнено у">
                  {п.fill_pct === null || п.fill_pct === undefined ? '—' : `${п.fill_pct} %`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <b>Как двигался документ</b>
      {к.stage_history.length ? (
        <div className="table-wrap cards" style={{ margin: '8px 0 12px' }}>
          <table>
            <thead><tr><th>Когда</th><th>Стадия</th></tr></thead>
            <tbody>
              {к.stage_history.map((п, i) => (
                <tr key={i}>
                  <td data-label="Когда">{fmtDate(п.created_time, true)}</td>
                  <td data-label="Стадия">{п.stage_id ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Пустая история без границ охвата читалась бы как «стадию не меняли», а это
        // другое утверждение — и по документам согласования оно опаснее прочерка.
        <div className="hint" style={{ margin: '8px 0 12px' }}>
          Переходов по этому документу зеркало не видело. Это не значит, что стадию не
          меняли: историю по этому виду документов синк читает
          {охват.first_at ? ` с ${fmtDate(охват.first_at)}` : ' не с начала'}
          {охват.rows ? `, всего ${pluralN(охват.rows, 'переход', 'перехода', 'переходов')}` : ''}
          {охват.owners ? ` по ${pluralN(охват.owners, 'документу', 'документам', 'документам')}` : ''}.
        </div>
      )}

      <b>Лента</b>
      {к.timeline.length ? (
        <div className="table-wrap cards" style={{ marginTop: 8 }}>
          <table>
            <thead><tr><th>Когда</th><th>Кто</th><th>Что записано</th></tr></thead>
            <tbody>
              {к.timeline.map((з) => (
                <tr key={з.bitrix_id}>
                  <td data-label="Когда">{fmtDate(з.created, true)}</td>
                  <td data-label="Кто">{з.author_name || '—'}</td>
                  <td data-label="Что записано">
                    {з.comment_text || <span className="muted">без текста</span>}
                    {/* Сколько вложений — видно, что именно вложено — нет. */}
                    {з.files_count > 0 && (
                      <div className="muted">
                        {pluralN(з.files_count, 'вложение', 'вложения', 'вложений')};
                        {' '}смотреть — в Bitrix
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="hint" style={{ marginTop: 8 }}>
          Комментариев по этому документу в зеркале нет.
        </div>
      )}
    </>
  )
}

/** Значение поля — в строку. Списки склеиваем, булево называем словом. */
function показать(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'да' : 'нет'
  if (Array.isArray(v)) return v.map((x) => показать(x)).join(' · ')
  if (typeof v === 'number') return String(v)
  return String(v)
}

// ═══════════════════════════════ сводка ═══════════════════════════════
//
// Разрез — стадия ВНУТРИ вида документа, а не стадия сама по себе: «Оплачен» у счёта
// клиенту и «Оплачен» у счёта поставщику — это деньги, которые нам должны, и деньги,
// которые должны мы. Сложить их в одну строку значило бы сложить долг с выручкой.

function СводкаПоСтадиям({ группы }: { группы: ГруппаСводки[] }) {
  const непустые = группы.filter((г) => г.docs > 0)
  if (!непустые.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Под этот отбор не попал ни один документ</div>
        <div className="empty-hint">
          Снимите фильтр по виду документа или расширьте период: сводка считается по тому
          же отбору, что и реестр.
        </div>
      </div>
    )
  }
  return (
    <>
      {непустые.map((г) => (
        <div className="card" key={г.kind} style={{ marginBottom: 12 }}>
          <div className="page-head" style={{ marginBottom: 8 }}>
            <b>{г.title}</b>
            <span className="spacer" />
            <span className="hint">
              {pluralN(г.docs, 'документ', 'документа', 'документов')}
              {г.has_money
                ? ` · ${fmtMoney(г.amount_sum)}`
                  + (г.no_amount > 0 ? ` · без суммы: ${г.no_amount}` : '')
                : ' · суммы у этого вида документов нет вовсе'}
            </span>
          </div>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Стадия</th>
                  <th className="num">Документов</th>
                  <th className="num">Сумма</th>
                  <th className="num">Из них на ноль</th>
                  <th className="num">Сделок</th>
                  <th>Последний</th>
                </tr>
              </thead>
              <tbody>
                {г.stages.map((с) => (
                  <tr key={с.stage_id ?? 'нет'}>
                    <td data-label="Стадия">{с.stage_name ?? с.stage_id ?? '—'}</td>
                    <td className="num" data-label="Документов">{с.docs}</td>
                    <td className="num" data-label="Сумма">
                      {с.amount_sum === null || с.amount_sum === undefined
                        ? <span className="muted">—</span>
                        : fmtMoney(с.amount_sum)}
                      {с.no_amount > 0 && (
                        <div className="muted">без суммы: {с.no_amount}</div>
                      )}
                    </td>
                    <td className="num" data-label="Из них на ноль">{с.zero_amount}</td>
                    <td className="num" data-label="Сделок">{с.deals}</td>
                    <td data-label="Последний">{fmtDate(с.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  )
}
